/**
 * DomainRanking Component
 *
 * Intro step where participants rank their familiarity with 9 topic categories.
 * Uses drag-and-drop interface for intuitive ranking.
 *
 * Rankings are used for topic selection:
 * - Less familiar categories → prioritized for learning
 */
import React, { useState } from "react";
import { usePlayer } from "@empirica/core/player/classic/react";

// 5 Topic Domains (15 topics, 3 per domain) - Learning Experiment
// These IDs must match topicSelector.js TOPIC_DOMAINS values
const ALL_CATEGORIES = [
  {
    id: "stem",
    name: "STEM",
    description: "Gas pressure, molar mass, simple harmonic motion",
    icon: "🧪",
    color: "cyan"
  },
  {
    id: "finance",
    name: "Finance",
    description: "Bonds, securities markets, taxation principles",
    icon: "💰",
    color: "green"
  },
  {
    id: "social-sciences",
    name: "Social Sciences",
    description: "Developmental psychology, learning, urban geography",
    icon: "🧠",
    color: "purple"
  },
  {
    id: "health",
    name: "Health",
    description: "Fluid & electrolytes, blood typing, nursing",
    icon: "🏥",
    color: "red"
  },
  {
    id: "work-certifications",
    name: "Work Certifications",
    description: "Electrical fundamentals, food safety, OSHA",
    icon: "📋",
    color: "orange"
  }
];

export function DomainRanking({ next }) {
  const player = usePlayer();

  const [rankedCategories, setRankedCategories] = useState(() => {
    return [...ALL_CATEGORIES].sort(() => Math.random() - 0.5);
  });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [error, setError] = useState("");

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    setTimeout(() => {
      e.target.classList.add("opacity-50");
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove("opacity-50");
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    setHasInteracted(true);
    setError("");

    const newOrder = [...rankedCategories];
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);
    setRankedCategories(newOrder);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const moveItem = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setHasInteracted(true);
    setError("");

    const newOrder = [...rankedCategories];
    const [item] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, item);
    setRankedCategories(newOrder);
  };

  const handleSubmit = () => {
    if (!hasInteracted) {
      setError("Please drag to reorder the topics based on your familiarity before continuing.");
      return;
    }

    const rankings = {};
    rankedCategories.forEach((category, index) => {
      rankings[category.id] = index + 1;
    });

    player.set("categoryRankings", rankings);
    player.set("rankingTimestamp", new Date().toISOString());
    player.set("rankedCategoryOrder", rankedCategories.map(c => c.id));

    console.log("[DomainRanking] Category rankings submitted:", rankings);
    next();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-3xl">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-8 py-6 text-white">
            <div className="flex items-center gap-4 mb-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                <BarChartIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Rank Your Familiarity</h1>
                <p className="text-blue-100 text-sm mt-1">5 Topic Domains</p>
              </div>
            </div>
            <p className="text-blue-100 text-sm leading-relaxed">
              Order these topics from{" "}
              <span className="font-semibold text-green-300">least familiar</span> (top) to{" "}
              <span className="font-semibold text-blue-200">most familiar</span> (bottom)
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 divide-x divide-gray-100 bg-gray-50 border-b border-gray-100">
            <QuickStat
              icon={<ArrowUpIcon />}
              label="Top = #1"
              value="Least Familiar"
              color="green"
            />
            <QuickStat
              icon={<GripIcon />}
              label="Drag to"
              value="Reorder"
              color="blue"
            />
            <QuickStat
              icon={<ArrowDownIcon />}
              label="Bottom = #5"
              value="Most Familiar"
              color="blue"
            />
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8">
            {/* Instruction Card */}
            <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <LightbulbIcon className="w-4 h-4 text-amber-600" />
                </div>
                <p className="text-sm text-amber-800">
                  <strong>How to rank:</strong> Drag each topic or use the arrows to reorder.
                  Place the topic you know <strong>least</strong> about at the top (#1).
                  You'll be assigned topics based on your ranking.
                </p>
              </div>
            </div>

            {/* Ranking List */}
            <div className="space-y-2 mb-6">
              {rankedCategories.map((category, index) => {
                const isFirst = index === 0;
                const isLast = index === rankedCategories.length - 1;
                const isDragging = draggedIndex === index;
                const isDragOver = dragOverIndex === index;

                return (
                  <div
                    key={category.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    className={`
                      relative cursor-grab active:cursor-grabbing
                      transition-all duration-200 ease-out
                      ${isDragging ? "opacity-50 scale-[1.02]" : ""}
                      ${isDragOver ? "transform translate-y-2" : ""}
                    `}
                  >
                    {isDragOver && draggedIndex !== null && draggedIndex > index && (
                      <div className="absolute -top-1 left-0 right-0 h-1 bg-blue-500 rounded-full" />
                    )}

                    <div
                      className={`
                        p-4 flex items-center gap-4 rounded-xl border-2 transition-all
                        ${isFirst ? "border-green-300 bg-green-50/50" : ""}
                        ${isLast ? "border-blue-300 bg-blue-50/50" : ""}
                        ${!isFirst && !isLast ? "border-gray-200 bg-white" : ""}
                        ${isDragOver ? "border-blue-400 border-2" : ""}
                        hover:shadow-md hover:border-gray-300
                      `}
                    >
                      {/* Rank Number */}
                      <div
                        className={`
                          w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold flex-shrink-0
                          ${isFirst ? "bg-green-500 text-white" : ""}
                          ${isLast ? "bg-blue-500 text-white" : ""}
                          ${!isFirst && !isLast ? "bg-gray-100 text-gray-600" : ""}
                        `}
                      >
                        {index + 1}
                      </div>

                      {/* Category Info */}
                      <div className="flex-grow min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{category.icon}</span>
                          <h3 className="font-semibold text-gray-900">{category.name}</h3>
                        </div>
                        <p className="text-sm text-gray-500">{category.description}</p>
                      </div>

                      {/* Drag Handle */}
                      <div className="flex-shrink-0 text-gray-300">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                        </svg>
                      </div>

                      {/* Up/Down Buttons */}
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => moveItem(index, Math.max(0, index - 1))}
                          disabled={index === 0}
                          className={`p-1.5 rounded-lg transition-colors ${
                            index === 0
                              ? "text-gray-200 cursor-not-allowed"
                              : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          }`}
                          aria-label="Move up"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => moveItem(index, Math.min(rankedCategories.length - 1, index + 1))}
                          disabled={index === rankedCategories.length - 1}
                          className={`p-1.5 rounded-lg transition-colors ${
                            index === rankedCategories.length - 1
                              ? "text-gray-200 cursor-not-allowed"
                              : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          }`}
                          aria-label="Move down"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {isDragOver && draggedIndex !== null && draggedIndex < index && (
                      <div className="absolute -bottom-1 left-0 right-0 h-1 bg-blue-500 rounded-full" />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <AlertIcon className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Interaction Reminder */}
            {!hasInteracted && !error && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <InfoIcon className="w-4 h-4 text-blue-600" />
                </div>
                <p className="text-sm text-blue-700">Drag or use arrows to reorder at least one topic before continuing</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!hasInteracted}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 ${
                hasInteracted
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {hasInteracted ? (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircleIcon className="w-5 h-5" />
                  Continue
                </span>
              ) : (
                "Please reorder topics to continue"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Quick Stat Component
function QuickStat({ icon, label, value, color = "blue" }) {
  const colorClasses = {
    green: "bg-green-100 text-green-600",
    blue: "bg-blue-100 text-blue-600",
  };

  return (
    <div className="py-4 px-3 text-center">
      <div className={`w-8 h-8 mx-auto mb-2 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
        {icon}
      </div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-gray-900">{value}</p>
    </div>
  );
}

// Icons
function BarChartIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function GripIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  );
}

function LightbulbIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
    </svg>
  );
}

function AlertIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

function InfoIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  );
}

function CheckCircleIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
